// PREZENCE — Admin Leave Management (Advanced — matches LAF form)
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import printLAF from '../../utils/printLAF';

const getInitials = n => (n||'').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
const TABS = ['pending','approved','rejected','all'];

export default function AdminLeaves() {
  const [leaves, setLeaves]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [reviewing, setReviewing] = useState(null);
  const [notes, setNotes]         = useState('');
  const [processing, setProcessing] = useState(false);
  const [empStats, setEmpStats]   = useState(null);

  const fetchLeaves = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/leaves/list-all?status=${activeTab}`);
      setLeaves(res.data.data || []);
    } catch { toast.error('Failed to load leave requests'); }
    finally { setLoading(false); }
  }, [activeTab]);

  useEffect(() => { fetchLeaves(); }, [fetchLeaves]);

  const fetchEmpStats = async (userId) => {
    try {
      const res = await api.get(`/leaves/stats?user_id=${userId}`);
      setEmpStats(res.data);
    } catch { setEmpStats(null); }
  };

  const openReview = (leave) => {
    setReviewing(leave);
    setNotes('');
    fetchEmpStats(leave.users?.id || leave.user_id);
  };

  const handleReview = async (status) => {
    if (!reviewing) return;
    if (status === 'rejected' && !notes.trim()) {
      toast.error('Please provide a reason for rejection'); return;
    }
    setProcessing(true);
    try {
      await api.patch(`/leaves/${reviewing.id}/review`, { status, review_notes: notes });
      toast.success(`Leave ${status} ✅`);
      setReviewing(null); setNotes(''); setEmpStats(null);
      fetchLeaves();
    } catch (e) {
      toast.error(e.response?.data?.error || `Failed to ${status}`);
    } finally { setProcessing(false); }
  };

  const statusBadge = s => ({ approved:'badge-green', rejected:'badge-red', pending:'badge-amber', cancelled:'badge-gray' })[s] || 'badge-gray';

  const pendingCount = activeTab === 'pending' ? leaves.length : null;

  return (
    <div style={{ animation:'fadeIn 0.4s ease' }}>
      <div className="page-header">
        <h1 className="page-title">Leave Management</h1>
        <p className="page-sub">Review and process employee leave applications</p>
      </div>

      {/* Summary stats row */}
      <div className="grid-4" style={{ marginBottom:24 }}>
        {[
          { label:'Pending',  value: leaves.filter(l=>l.status==='pending').length,  color:'#f59e0b', tab:'pending'  },
          { label:'Approved', value: leaves.filter(l=>l.status==='approved').length, color:'#22c55e', tab:'approved' },
          { label:'Rejected', value: leaves.filter(l=>l.status==='rejected').length, color:'#ef4444', tab:'rejected' },
          { label:'Total',    value: leaves.length,                                   color:'#3ba8ff', tab:'all'      },
        ].map((s,i) => (
          <div key={i} className="card" style={{ padding:'16px 20px', cursor:'pointer', border:`1px solid ${activeTab===s.tab?`${s.color}40`:'rgba(56,189,248,0.1)'}` }}
            onClick={() => setActiveTab(s.tab)}>
            <p style={{ fontSize:10, color:'#475569', fontFamily:"'JetBrains Mono'", marginBottom:6 }}>{s.label.toUpperCase()}</p>
            <p style={{ fontSize:28, fontWeight:800, color:s.color, fontFamily:"'JetBrains Mono'" }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display:'flex', gap:8, marginBottom:20, borderBottom:'1px solid rgba(56,189,248,0.08)', paddingBottom:12 }}>
        {TABS.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            background: activeTab===tab ? 'rgba(99,220,188,0.12)' : 'transparent',
            border: `1px solid ${activeTab===tab ? 'rgba(99,220,188,0.35)' : 'transparent'}`,
            color: activeTab===tab ? '#63dcbc' : '#64748b',
            borderRadius:8, padding:'7px 18px', cursor:'pointer',
            fontSize:12, fontWeight:600, fontFamily:"'DM Sans',sans-serif",
            textTransform:'capitalize', transition:'all 0.2s',
          }}>
            {tab === 'all' ? 'All Leaves' : tab.charAt(0).toUpperCase()+tab.slice(1)}
            {tab === 'pending' && pendingCount > 0 && (
              <span style={{ marginLeft:6, background:'#f59e0b', color:'#05090f', borderRadius:20, padding:'1px 7px', fontSize:10, fontWeight:800 }}>{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* Leaves list */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">{activeTab==='all'?'ALL APPLICATIONS':`${activeTab.toUpperCase()} REQUESTS`}</span>
          <span style={{ fontSize:12, color:'#475569' }}>{leaves.length} records</span>
        </div>

        {loading ? (
          <div style={{ display:'flex', justifyContent:'center', padding:48 }}><div className="spinner" style={{width:28,height:28}} /></div>
        ) : leaves.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <div className="empty-state-text">No {activeTab==='all'?'':activeTab} leave requests found.</div>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {leaves.map(leave => (
              <div key={leave.id} style={{
                background:'rgba(255,255,255,0.02)',
                border:`1px solid ${leave.status==='pending'?'rgba(245,158,11,0.2)':leave.status==='approved'?'rgba(34,197,94,0.15)':leave.status==='rejected'?'rgba(239,68,68,0.1)':'rgba(56,189,248,0.07)'}`,
                borderRadius:12, padding:'18px 20px',
                transition:'border-color 0.2s',
              }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:14, flexWrap:'wrap' }}>
                  {/* Avatar */}
                  <div className="avatar avatar-md">{getInitials(leave.users?.full_name || leave.user?.full_name)}</div>

                  {/* Main content */}
                  <div style={{ flex:1, minWidth:200 }}>
                    {/* Name + status */}
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                      <span style={{ fontSize:14, fontWeight:700, color:'#e2e8f0' }}>
                        {leave.users?.full_name || leave.user?.full_name || 'Employee'}
                      </span>
                      <span className={`badge ${statusBadge(leave.status)}`}>
                        {leave.status.charAt(0).toUpperCase()+leave.status.slice(1)}
                      </span>
                      {leave.is_half_day && <span className="badge badge-blue" style={{ fontSize:9 }}>Half Day</span>}
                      {leave.leave_types?.is_paid === false && <span className="badge badge-red" style={{ fontSize:9 }}>Unpaid</span>}
                    </div>
                    <div style={{ fontSize:11, color:'#475569', marginBottom:12 }}>
                      {leave.users?.department || leave.user?.department} · {leave.users?.designation || leave.user?.designation}
                      {leave.section && ` · Section: ${leave.section}`}
                    </div>

                    {/* Leave details grid — like LAF form */}
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px,1fr))', gap:12, marginBottom:12 }}>
                      <div>
                        <div style={{ fontSize:9, color:'#334155', fontFamily:"'JetBrains Mono'", marginBottom:3 }}>LEAVE TYPE</div>
                        <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                          <div style={{ width:7,height:7,borderRadius:'50%',background:leave.leave_types?.color||'#63dcbc' }} />
                          <span style={{ fontSize:12, fontWeight:600, color:'#94a3b8' }}>{leave.leave_types?.name || leave.leave_type?.name || '—'}</span>
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize:9, color:'#334155', fontFamily:"'JetBrains Mono'", marginBottom:3 }}>PERIOD</div>
                        <div style={{ fontSize:12, fontWeight:600, color:'#94a3b8' }}>
                          {new Date(leave.from_date).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}
                          {leave.from_date !== leave.to_date && ` → ${new Date(leave.to_date).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}`}
                          <span style={{ color:'#63dcbc', marginLeft:6, fontFamily:"'JetBrains Mono'" }}>({leave.total_days}d)</span>
                          {leave.is_half_day && <span style={{ marginLeft:6, fontSize:9, fontWeight:700, padding:'1px 7px', borderRadius:99, background:'rgba(59,168,255,0.12)', border:'1px solid rgba(59,168,255,0.35)', color:'#3ba8ff', fontFamily:"'JetBrains Mono'" }}>Half Day ({leave.half_day_session || 'morning'})</span>}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize:9, color:'#334155', fontFamily:"'JetBrains Mono'", marginBottom:3 }}>APPLIED ON</div>
                        <div style={{ fontSize:12, color:'#64748b' }}>{new Date(leave.applied_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</div>
                      </div>
                      {leave.contact_during_leave && (
                        <div>
                          <div style={{ fontSize:9, color:'#334155', fontFamily:"'JetBrains Mono'", marginBottom:3 }}>CONTACT</div>
                          <div style={{ fontSize:12, color:'#64748b' }}>{leave.contact_during_leave}</div>
                        </div>
                      )}
                    </div>

                    {/* Reason */}
                    <div style={{ padding:'8px 12px', background:'rgba(255,255,255,0.02)', borderRadius:8, borderLeft:'2px solid rgba(99,220,188,0.25)', marginBottom:8 }}>
                      <div style={{ fontSize:9, color:'#334155', fontFamily:"'JetBrains Mono'", marginBottom:2 }}>REASON OF LEAVE</div>
                      <div style={{ fontSize:12, color:'#94a3b8' }}>{leave.reason}</div>
                    </div>

                    {/* Duty delegation */}
                    {(leave.duty_handover_user || leave.users?.duty_handover) && (
                      <div style={{ fontSize:11, color:'#64748b', marginBottom:8 }}>
                        👤 Duties covered by: <span style={{ color:'#94a3b8', fontWeight:600 }}>
                          {leave.duty_handover_user?.full_name || '—'}
                        </span>
                        {leave.duty_handover_notes && <span style={{ color:'#475569' }}> — {leave.duty_handover_notes}</span>}
                      </div>
                    )}

                    {/* HR review note */}
                    {leave.review_notes && (
                      <div style={{ padding:'8px 12px', background:leave.status==='approved'?'rgba(34,197,94,0.06)':'rgba(239,68,68,0.06)', borderRadius:8, borderLeft:`3px solid ${leave.status==='approved'?'#22c55e':'#ef4444'}` }}>
                        <div style={{ fontSize:9, color:leave.status==='approved'?'#22c55e':'#ef4444', fontFamily:"'JetBrains Mono'", marginBottom:2 }}>HR/ADMIN NOTE</div>
                        <div style={{ fontSize:12, color:'#94a3b8' }}>{leave.review_notes}</div>
                        {leave.reviewer && (
                          <div style={{ fontSize:10, color:'#334155', marginTop:3 }}>
                            — {leave.reviewer.full_name} · {new Date(leave.reviewed_at).toLocaleDateString('en-IN')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div style={{ flexShrink:0, display:'flex', gap:8 }}>
                    {leave.status === 'pending' && (
                      <button className="btn btn-primary btn-sm" onClick={() => openReview(leave)}>
                        Review
                      </button>
                    )}
                    <button className="btn btn-secondary btn-sm" title="Print Leave Application Form"
                      onClick={() => printLAF(leave, leave.users || leave.user)}>
                      🖨 LAF
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── REVIEW MODAL ── */}
      {reviewing && (
        <div className="modal-overlay" onClick={() => { setReviewing(null); setEmpStats(null); }}>
          <div className="modal-box" style={{ maxWidth:600 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Review Leave Application</h2>
              <button className="modal-close" onClick={() => { setReviewing(null); setEmpStats(null); }}>✕</button>
            </div>

            {/* Leave summary */}
            <div style={{ padding:'14px 16px', background:'rgba(99,220,188,0.05)', border:'1px solid rgba(99,220,188,0.12)', borderRadius:10, marginBottom:20 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:8 }}>
                <div>
                  <div style={{ fontSize:14, fontWeight:700, color:'#e2e8f0', marginBottom:4 }}>
                    {reviewing.users?.full_name || reviewing.user?.full_name}
                  </div>
                  <div style={{ fontSize:12, color:'#64748b' }}>
                    {reviewing.leave_types?.name || reviewing.leave_type?.name} · {reviewing.total_days} day{reviewing.total_days>1?'s':''}
                    {reviewing.is_half_day && (
                      <span style={{ marginLeft:8, fontSize:10, fontWeight:700, padding:'2px 9px', borderRadius:99, background:'rgba(59,168,255,0.12)', border:'1px solid rgba(59,168,255,0.35)', color:'#3ba8ff', fontFamily:"'JetBrains Mono'" }}>
                        Half Day ({reviewing.half_day_session || 'morning'})
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize:12, color:'#64748b' }}>
                    {new Date(reviewing.from_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}
                    {reviewing.from_date !== reviewing.to_date && ` to ${new Date(reviewing.to_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}`}
                  </div>
                  <div style={{ fontSize:12, color:'#94a3b8', marginTop:6, fontStyle:'italic' }}>"{reviewing.reason}"</div>
                </div>
                {reviewing.contact_during_leave && (
                  <div style={{ fontSize:11, color:'#475569' }}>📞 {reviewing.contact_during_leave}</div>
                )}
              </div>
            </div>

            {/* Employee leave stats — the LAF table */}
            {empStats?.stats?.length > 0 && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:10, color:'#475569', fontFamily:"'JetBrains Mono'", letterSpacing:'0.1em', marginBottom:10 }}>EMPLOYEE LEAVE STATEMENT — {empStats.year}</div>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                    <thead>
                      <tr style={{ background:'rgba(56,189,248,0.06)', borderBottom:'1px solid rgba(56,189,248,0.1)' }}>
                        {['Type','Total','Availed','This Month','Balance','Applied Now'].map(h => (
                          <th key={h} style={{ padding:'7px 10px', textAlign:'left', color:'#475569', fontWeight:700, fontFamily:"'JetBrains Mono'", fontSize:9, letterSpacing:'0.08em' }}>{h.toUpperCase()}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {empStats.stats.map((s, i) => {
                        const isCurrentType = s.leave_type_id === reviewing.leave_type_id;
                        return (
                          <tr key={i} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)', background:isCurrentType?'rgba(99,220,188,0.05)':'transparent' }}>
                            <td style={{ padding:'7px 10px' }}>
                              <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                                <div style={{ width:6,height:6,borderRadius:'50%',background:s.color }} />
                                <span style={{ color: isCurrentType ? '#63dcbc' : '#94a3b8', fontWeight: isCurrentType ? 700 : 400 }}>{s.name}</span>
                              </div>
                            </td>
                            <td style={{ padding:'7px 10px', color:'#94a3b8', fontFamily:"'JetBrains Mono'" }}>{s.allocated}</td>
                            <td style={{ padding:'7px 10px', color:'#f59e0b', fontFamily:"'JetBrains Mono'" }}>{s.availed}</td>
                            <td style={{ padding:'7px 10px', color:'#3ba8ff', fontFamily:"'JetBrains Mono'" }}>{s.month_taken}</td>
                            <td style={{ padding:'7px 10px', fontFamily:"'JetBrains Mono'", fontWeight:700, color:s.balance<=2?'#ef4444':s.balance<=5?'#f59e0b':'#22c55e' }}>{s.balance}</td>
                            <td style={{ padding:'7px 10px', color:'#63dcbc', fontFamily:"'JetBrains Mono'" }}>
                              {isCurrentType ? reviewing.total_days : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div style={{ display:'flex', gap:20, marginTop:8, fontSize:11, color:'#475569' }}>
                  <span>📅 Year total taken: <strong style={{ color:'#f59e0b' }}>{empStats.totalYearTaken} days</strong></span>
                  <span>📆 This month: <strong style={{ color:'#3ba8ff' }}>{empStats.totalMonthTaken} days</strong></span>
                </div>
              </div>
            )}

            {/* HR Notes */}
            <div className="form-group">
              <label className="form-label">HR / ADMIN NOTE TO EMPLOYEE</label>
              <textarea
                className="form-textarea"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder={`e.g. "This leave cannot be granted as a high-value delegate is visiting on Monday. Please reschedule if possible." — Required for rejection`}
                style={{ minHeight:90 }}
              />
              <div style={{ fontSize:10, color:'#475569', marginTop:4 }}>Note is required for rejection. Optional for approval.</div>
            </div>

            {/* Action buttons — like Chairman/MD signature line */}
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20, paddingTop:16, borderTop:'1px solid rgba(255,255,255,0.06)' }}>
              <button className="btn btn-secondary" onClick={() => { setReviewing(null); setEmpStats(null); }} disabled={processing}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleReview('rejected')} disabled={processing || !notes.trim()}>
                {processing ? <div className="spinner" style={{width:14,height:14}} /> : '✕ Not Approved'}
              </button>
              <button className="btn btn-primary" style={{ background:'linear-gradient(135deg,#22c55e,#16a34a)' }} onClick={() => handleReview('approved')} disabled={processing}>
                {processing ? <div className="spinner" style={{width:14,height:14}} /> : '✓ Approved'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
